// ─────────────────────────────────────────────────────────────────────────────
// Team-switch navigation helper (Apr 30 2026, rewritten 2026-09-16)
//
// Fixes a coach-reported bug: switching teams from the sidebar updated
// `activeTeam` state but left the URL pointing at the OLD team id. Pages
// like TeamPage that use `useParams().teamId` kept rendering the old
// team's data until the user manually navigated away.
//
// `pathForTeamSwitch` rewrites the URL so the new team's equivalent
// page loads. For paths that don't translate (a specific game id from
// team A doesn't exist for team B) it falls back to the dashboard.
//
// Used by Desktop / Tablet / Mobile / Capacitor layouts in their
// sidebar team-button onClick handlers.
//
// 2026-09-16 — BUG: switching teams from Schedule or Seasons produced a
// 404 ("Puck's gone missing"). Two root causes, both in this file:
//   1. The translatable list was written against nav LABELS, not the
//      real route segments in main.jsx: the Schedule page lives at
//      `/team/:id/games` (not `/schedule`), and `seasons`, `my-stats`,
//      `media`, `notification-log` were missing entirely. Anything not
//      in the list fell through to the fallback…
//   2. …and the fallback was `/team/:newId/dashboard`, which is NOT a
//      route (`/team/:id` redirects to settings; the dashboard is the
//      top-level `/dashboard`). So the fallback itself 404'd.
// The list below is now the exact set of first segments under
// `team/:teamId/` in main.jsx. The fallback is `/dashboard`.
//
// Also new: an optional 4th argument — the destination team object
// (`{ member_role, settings }` as returned by GET /teams). When given,
// coach-only pages are re-targeted for a user who is a plain PLAYER on
// the destination team (a coach on team A can be a skater on team B):
// Stats ↔ My Stats swap, and Players / Seasons / Finance / Budget /
// Notification Log / Team Settings go to /dashboard instead of a page
// that would 403 server-side. Feature-gated pages (Scoreboards, Budget)
// respect the destination team's settings the same way the sidebar
// does. Callers that don't pass the team keep the old (role-blind)
// behaviour.
// ─────────────────────────────────────────────────────────────────────────────

// Every first path segment that exists under `team/:teamId/` in
// main.jsx. Keep in sync with the router — an entry missing here means
// "fall back to /dashboard" on switch, an entry here that isn't a route
// means a 404.
const TEAM_ROUTE_SEGMENTS = new Set([
  'games',            // Schedule list (`/games/import` and `/games/:id/…` are handled below)
  'players',          // Roster (`/players/import` → `/players`)
  'seasons',
  'chat',
  'stats',            // coach/manager Stats
  'my-stats',         // player Stats
  'scoreboards',
  'media',
  'finance',
  'budget',
  'notification-log',
  'settings',
]);

// Pages the sidebar only shows to coach/manager (mirrors the
// `isCoachish` gating in DesktopLayout's navItems).
const COACH_ONLY_SEGMENTS = new Set([
  'players', 'seasons', 'stats', 'finance', 'budget', 'notification-log', 'settings',
]);

const isCoachish = (team) =>
  !!team && ['coach', 'manager'].includes(team.member_role);

/**
 * Compute the URL to navigate to after the user picks a new team.
 *
 * @param {string} pathname  Current `location.pathname`.
 * @param {string} oldTeamId Currently-active team id (string in URL).
 * @param {string} newTeamId Newly-picked team id.
 * @param {object} [newTeam] Optional destination team object
 *                            (`{ id, member_role, settings }`). When
 *                            supplied, the result is role- and
 *                            feature-aware (see header comment).
 * @returns {string|null}    Path to navigate to, or null if no
 *                            navigation is needed (path doesn't
 *                            reference a team).
 */
export function pathForTeamSwitch(pathname, oldTeamId, newTeamId, newTeam = null) {
  if (!pathname || !newTeamId) return null;

  // Only act on /team/:id/... paths. Pages outside the team scope
  // (e.g. /admin, /profile, /dashboard) shouldn't navigate when
  // switching team — the layout's activeTeam state carries the switch.
  const m = (pathname || '').match(/^\/team\/([^/]+)(?:\/(.*))?$/);
  if (!m) return null;
  const subpath = m[2] || '';

  // If the URL doesn't actually carry the old team id (rare — direct
  // load from a deep link), still rewrite using the matched id so the
  // user lands on the new team's same page.
  void oldTeamId;

  // Role / feature gating is only possible when the caller handed us
  // the destination team. `null` team → behave exactly as before.
  const coachish = newTeam ? isCoachish(newTeam) : null; // null = unknown

  let firstSeg = subpath.split('/')[0] || '';
  // Bare `/team/:id` is a legacy alias that redirects to settings —
  // treat it as settings so the redirect target is role-checked too.
  if (firstSeg === '') firstSeg = 'settings';

  // Settings: /settings/<section> is fully translatable. The settings
  // sub-nav has the same set of sections for every team.
  const settingsMatch = subpath.match(/^settings(?:\/([^/]+))?$/);
  if (settingsMatch || firstSeg === 'settings') {
    if (coachish === false) return '/dashboard'; // players have no Team Settings
    return settingsMatch && settingsMatch[1]
      ? `/team/${newTeamId}/settings/${settingsMatch[1]}`
      : `/team/${newTeamId}/settings/team-info`;
  }

  if (!TEAM_ROUTE_SEGMENTS.has(firstSeg)) {
    // Unknown segment (shouldn't happen — every route is listed
    // above). Land somewhere that definitely exists.
    return '/dashboard';
  }

  // Deep paths with resource ids (e.g. /games/:gameId/scoreboard) don't
  // translate — a team-A game id doesn't exist on team B. The import
  // wizards (/games/import, /players/import) are per-team wizards, so
  // restart them at the parent list rather than mid-wizard.
  // Result: any `/games/...` or `/players/...` collapses to its list.
  let target = firstSeg;

  if (coachish === false) {
    // Destination team: the user is a player/spare there.
    if (target === 'stats') target = 'my-stats';
    else if (COACH_ONLY_SEGMENTS.has(target)) return '/dashboard';
  } else if (coachish === true) {
    // Destination team: coach/manager. The player-scoped Stats page
    // isn't in their sidebar — send them to the full Stats page.
    if (target === 'my-stats') target = 'stats';
  }

  // Feature-gated pages: mirror the sidebar's visibility rules so we
  // never navigate to a page the nav wouldn't show for that team.
  if (newTeam) {
    const s = newTeam.settings || {};
    if (target === 'scoreboards' && s.scoreboard_enabled === false) return '/dashboard';
    if (target === 'budget' && s.budget_enabled !== true) return '/dashboard';
  }

  return `/team/${newTeamId}/${target}`;
}
